// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import { createWebSocketMiddleware } from "../../web-utils/src/store/Websocket/WebsocketMiddleware";
import { appLogoutReset } from "./globalActions";
import wsEventHandlers from "./websocket/WebsocketHandler";

import userReducer from "./user/userSlice";
import studioReducer from "./studio/studioSlice";
import scenesReducer from "./scenes/scenesSlice";
import companiesReducer from "./companies/companiesSlice";
import showsReducer from "./shows/showsSlice";
import equipmentReducer from "./equipment/equipmentSlice";
import scriptsReducer from "./scripts/scriptsSlices";

// shared reducer for websocket state management
import webSocketSlice from "../../web-utils/src/store/Websocket/WebsocketSlice";

const BUILD_ID = process.env.GIT_BUILD || "1";

// Combine multiple slices into a single reducer
const appReducer = combineReducers({
  user: userReducer,
  companies: companiesReducer,
  shows: showsReducer,
  equipment: equipmentReducer,
  scripts: scriptsReducer,
  scenes: scenesReducer,
  studio: studioReducer,
  // web-utils shared state
  webSocket: webSocketSlice,
});

const rootReducer = (state, action) => {
  if (action.type === appLogoutReset.type) {
    state = undefined;
  }

  return appReducer(state, action);
};

// Create websocket middleware with shared event handlers
const websocketMiddleware = createWebSocketMiddleware(wsEventHandlers);

// Persist configuration for the 'user' slice
const persistConfig = {
  key: "root",
  version: BUILD_ID, // clear redux cached state on deployment
  migrate: async (inboundState) => {
    // If the version doesn't match, return the original state
    const prev = inboundState?._persist?.version;
    return prev === BUILD_ID ? inboundState : undefined;
  },
  storage,
  whitelist: ["companies", "shows", "studio", "scripts", "equipment", "scenes"], // Only cache reduces in the array
};

// Create a persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure the store with the persisted reducer
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false, // Disable ImmutableStateInvariantMiddleware
      serializableCheck: false, // Disable SerializableStateInvariantMiddleware
    }).concat(websocketMiddleware),
});

// Export persistor for use in your application
export const persistor = persistStore(store);
